import React, { useState, useEffect, memo } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveSubmenuItem } from '../../store/slices/menuSlice';
import { useThemeColors } from '../../hooks/useThemeColors';
import { fetchGreyMenuColor, updateGreyMenuDisplayMode } from '../../store/slices/usersSlice';

// Memoize individual menu items to prevent unnecessary re-renders
const MenuItem = memo(({ item, index, activeSubmenuItem, hoverIndex, setHoverIndex, handleItemClick, mounted, greyMenuColors, displayMode }) => {
  const baseClasses = `group relative flex items-center ${displayMode === 'icon' ? 'justify-center px-3 py-3' : 'gap-3 px-4 py-3'} rounded-lg 
    transition-all duration-200 ease-out
    ${item.highlight
      ? 'bg-blue-600 hover:bg-blue-700 shadow-sm'
      : activeSubmenuItem === item.link 
        ? 'bg-gray-50' 
        : 'hover:bg-gray-50'
    }
    transform ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
    transition-transform transition-opacity duration-300 ease-out`;

  const iconClasses = `transition-colors duration-200 ${
    item.highlight 
      ? "text-white" 
      : activeSubmenuItem === item.link 
        ? "text-gray-900" 
        : "text-gray-500 group-hover:text-gray-900"
  }`;

  const textClasses = `font-medium text-sm transition-colors duration-200 ${
    item.highlight 
      ? "text-white" 
      : activeSubmenuItem === item.link 
        ? "text-gray-900" 
        : "text-gray-500 group-hover:text-gray-900"
  }`;

  if (item.onClick) {
    return (
      <div
        key={index}
        onClick={() => {
          item.onClick();
          handleItemClick(item.link);
        }}
        onMouseEnter={() => setHoverIndex(index)}
        onMouseLeave={() => setHoverIndex(null)}
        className={`cursor-pointer ${baseClasses}`}
        style={{ transitionDelay: `${index * 100}ms` }}
      >
        <div className={iconClasses}>
          {item.icon}
        </div>
        {displayMode === 'text' && (
          <span className={textClasses}>
            {item.text}
          </span>
        )}
      </div>
    );
  }
  
  return (
    <Link 
      key={index} 
      to={item.link} 
      onMouseEnter={() => setHoverIndex(index)}
      onMouseLeave={() => setHoverIndex(null)}
      className={baseClasses}
      style={{ transitionDelay: `${index * 100}ms` }}
      onClick={() => handleItemClick(item.link)}
    >
      <div className={iconClasses}>
        {item.icon}
      </div>
      {displayMode === 'text' && (
        <span className={textClasses}>
          {item.text}
        </span>
      )}
    </Link>
  );
});

// Main GreyMenu component
const GreyMenu = memo(({ onNewLead }) => {
  const dispatch = useDispatch();
  const { showGreyMenu, activeSection, activeSubmenuItem } = useSelector(state => state.menu);
  const { greyMenuDisplayMode } = useSelector(state => state.users);
  const { user } = useSelector(state => state.auth);
  const { greyMenuColors } = useThemeColors();
  const [hoverIndex, setHoverIndex] = useState(null);
  const [mounted, setMounted] = useState(false);

  // Add animation delay for menu items
  useEffect(() => {
    const timer = requestAnimationFrame(() => {
      setMounted(true);
    });
    return () => cancelAnimationFrame(timer);
  }, []);

  // Fetch grey menu color on component mount
  useEffect(() => {
    dispatch(fetchGreyMenuColor());
  }, [dispatch]);

  const menuItems = {
    leads: [
      {
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12S6.48 22 12 22 22 17.52 22 12 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"/>
            <circle cx="12" cy="12" r="3" opacity="0.3"/>
          </svg>
        ),
        text: "New Lead",
        link: "#",
        highlight: true,
        onClick: () => onNewLead && onNewLead()
      },
      {
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20ZM8 15.01L8.01 15H16V17H8V15.01ZM8 12.01L8.01 12H16V14H8V12.01ZM8 9.01L8.01 9H12V11H8V9.01Z"/>
          </svg>
        ),
        text: "Open Leads",
        link: "/leads"
      },
      {
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2Z"/>
            <circle cx="12" cy="9" r="2.5" fill="white"/>
          </svg>
        ),
        text: "Map View",
        link: "/leads/map"
      }
    ],
    projects: [
      {
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19Z"/>
            <path d="M7 7H17V9H7V7ZM7 11H17V13H7V11ZM7 15H14V17H7V15Z" fill="white"/>
          </svg>
        ),
        text: "Projects",
        link: "/projects",
        highlight: true
      },
      {
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2Z"/>
            <circle cx="12" cy="9" r="2.5" fill="white"/>
          </svg>
        ),
        text: "Map View",
        link: "/projects/map"
      }
    ]
  };

  const currentMenuItems = activeSection ? menuItems[activeSection] || [] : [];

  const handleItemClick = (link) => {
    dispatch(setActiveSubmenuItem(link));
  };

  const toggleDisplayMode = () => {
    const newMode = greyMenuDisplayMode === 'icon' ? 'text' : 'icon';
    dispatch(updateGreyMenuDisplayMode({ greyMenuDisplayMode: newMode }));
  };

  if (!showGreyMenu) return null;

  return (
    <>
      {/* Desktop Grey Menu - New design */}
      <aside 
        className={`fixed left-[64px] md:left-[64px] lg:left-[72px] xl:left-[80px] top-0 h-screen 
        ${greyMenuDisplayMode === 'icon' ? 'w-[80px]' : 'w-[280px]'} 
        transform transition-all duration-300 ease-in-out bg-white
        ${showGreyMenu ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'}
        hidden md:block z-10`}
      >
        {/* Company Name */}
        <div className="flex items-center px-6 py-8 border-b border-gray-100">
          {greyMenuDisplayMode === 'text' && (
            <div>
              <h1 className="font-semibold text-gray-900">JRT DRIVER</h1>
              <p className="text-xs text-gray-500">Project Management</p>
            </div>
          )}
        </div>

        {/* Menu Header */}
        <div className="flex items-center justify-between px-6 py-6">
          {greyMenuDisplayMode === 'text' && (
            <h2 className="text-sm font-medium text-gray-500">
              {activeSection === 'leads' ? 'Leads & Estimates' : 'Project Management'}
            </h2>
          )}
          
          {/* Toggle Button */}
          <button
            onClick={toggleDisplayMode}
            className="p-2 rounded-lg transition-all duration-200 hover:bg-gray-100"
            title={greyMenuDisplayMode === 'icon' ? 'Switch to text mode' : 'Switch to icon mode'}
          >
            {greyMenuDisplayMode === 'icon' ? (
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Menu Items */}
        <nav className="px-3 mt-2">
          {currentMenuItems.map((item, index) => (
            <MenuItem 
              key={index}
              item={item}
              index={index}
              activeSubmenuItem={activeSubmenuItem}
              hoverIndex={hoverIndex}
              setHoverIndex={setHoverIndex}
              handleItemClick={handleItemClick}
              mounted={mounted}
              greyMenuColors={greyMenuColors}
              displayMode={greyMenuDisplayMode}
            />
          ))}
        </nav>

        {/* User Profile Section */}
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-gray-100">
          <div className="flex items-center">
            {user?.profile?.profilePicture ? (
              <img 
                src={user.profile.profilePicture}
                alt={`${user.profile.firstName} ${user.profile.lastName}`}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            )}
            {greyMenuDisplayMode === 'text' && (
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {user?.profile?.firstName} {user?.profile?.lastName}
                </p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Sub Bottom Navigation */}
      <nav className="fixed bottom-16 left-0 right-0 bg-white border-t border-gray-100 md:hidden z-20">
        <div className="flex justify-around items-center py-2 px-4">
          {currentMenuItems.map((item, index) => (
            item.onClick ? (
              <button
                key={index}
                onClick={() => {
                  item.onClick();
                  handleItemClick(item.link);
                }}
                className={`flex items-center justify-center p-3 rounded-lg transition-all duration-200
                  ${activeSubmenuItem === item.link 
                    ? 'bg-gray-100' 
                    : 'hover:bg-gray-50'
                  }`}
              >
                <div className={`${item.highlight ? "text-blue-600" : activeSubmenuItem === item.link ? "text-gray-900" : "text-gray-500"}`}>
                  {item.icon}
                </div>
              </button>
            ) : (
              <Link 
                key={index} 
                to={item.link} 
                className={`flex items-center justify-center p-3 rounded-lg transition-all duration-200
                  ${activeSubmenuItem === item.link 
                    ? 'bg-gray-100' 
                    : 'hover:bg-gray-50'
                  }`}
                onClick={() => handleItemClick(item.link)}
              >
                <div className={`${item.highlight ? "text-blue-600" : activeSubmenuItem === item.link ? "text-gray-900" : "text-gray-500"}`}>
                  {item.icon}
                </div>
              </Link>
            )
          ))}
        </div>
      </nav>
    </>
  );
});

export default GreyMenu; 